import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Coins as CoinsIcon, Play, ArrowLeft, Sparkles, Loader2, Check, Gift, Zap, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";

export default function Coins() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [packs, setPacks] = useState([]);
  const [watching, setWatching] = useState(false);
  const [buying, setBuying] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [history, setHistory] = useState([]);
  const [claiming, setClaiming] = useState(false);
  const [spending, setSpending] = useState(false);

  const loadHistory = () => api.get("/coins/history").then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/coins/packs").then((r) => setPacks(r.data));
    loadHistory();
  }, []);

  const claimDaily = async () => {
    setClaiming(true);
    try {
      const res = await api.post("/coins/daily");
      await refreshUser();
      loadHistory();
      toast.success(`Täglicher Bonus: +${res.data.earned} Münzen! 🎁`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bonus nicht verfügbar");
    } finally {
      setClaiming(false);
    }
  };

  const spendMessages = async () => {
    setSpending(true);
    try {
      const res = await api.post("/coins/spend/messages");
      await refreshUser();
      loadHistory();
      toast.success(`+${res.data.added_messages} Nachrichten heute!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nicht genügend Münzen");
    } finally {
      setSpending(false);
    }
  };

  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("cancelled")) {
      toast.info("Kauf abgebrochen");
      setParams({}, { replace: true });
      return;
    }
    if (!sid) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/coins/purchase/status/${sid}`);
        if (r.data.payment_status === "paid") {
          await refreshUser();
          toast.success(`${r.data.coins} Münzen gutgeschrieben! 🎉`);
          setVerifying(false);
          setParams({}, { replace: true });
          return;
        }
      } catch (e) {}
      if (attempts >= 10) {
        setVerifying(false);
        setParams({}, { replace: true });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const watchAd = async () => {
    setWatching(true);
    // simulate a short ad
    await new Promise((r) => setTimeout(r, 1800));
    try {
      const res = await api.post("/coins/earn-ad");
      await refreshUser();
      toast.success(`+${res.data.earned} Münzen!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Werbung fehlgeschlagen");
    } finally {
      setWatching(false);
    }
  };

  const buy = async (pack) => {
    setBuying(pack.id);
    try {
      const res = await api.post(`/coins/purchase?pack_id=${pack.id}`, { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Kauf konnte nicht gestartet werden");
      setBuying(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <button
          data-testid="coins-back"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-all"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>

        <div className="text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-gradient-to-r from-amber-500/15 to-yellow-500/15 border border-amber-400/30"
          >
            <CoinsIcon className="w-7 h-7 text-amber-300" />
            <span className="font-display text-3xl font-extrabold text-white" data-testid="coins-balance">
              {verifying ? "..." : (user?.coins ?? 0).toLocaleString("de-DE")}
            </span>
            <span className="text-amber-200/70 text-sm font-medium">Münzen</span>
          </motion.div>
          <p className="mt-3 text-slate-400 text-sm">Verdiene Münzen durch Werbung oder kaufe sie dir dazu.</p>
        </div>

        {/* Earn via ad */}
        <div className="mt-8 rounded-3xl bg-[#12141D] border border-white/10 p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
            <Play className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-white">Werbung ansehen</h3>
            <p className="text-sm text-slate-400">+25 Münzen pro Werbespot</p>
          </div>
          <button
            data-testid="watch-ad-btn"
            onClick={watchAd}
            disabled={watching}
            className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl px-5 py-3 shadow-lg shadow-emerald-500/20 active:scale-95 transition-all disabled:opacity-60"
          >
            {watching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {watching ? "Läuft..." : "Ansehen"}
          </button>
        </div>

        {/* Daily bonus + spend */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-3xl bg-[#12141D] border border-white/10 p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-white">Täglicher Bonus</h3>
              <p className="text-sm text-slate-400">+50 Münzen pro Tag</p>
            </div>
            <button
              data-testid="daily-bonus-btn"
              onClick={claimDaily}
              disabled={claiming || user?.daily_claimed}
              className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl px-4 py-3 active:scale-95 transition-all disabled:opacity-50"
            >
              {user?.daily_claimed ? "Abgeholt ✓" : claiming ? "..." : "Abholen"}
            </button>
          </div>

          <div className="rounded-3xl bg-[#12141D] border border-white/10 p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-white">Extra-Nachrichten</h3>
              <p className="text-sm text-slate-400">+100 Nachrichten für 50 Münzen</p>
            </div>
            <button
              data-testid="spend-messages-btn"
              onClick={spendMessages}
              disabled={spending}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold rounded-xl px-4 py-3 active:scale-95 transition-all disabled:opacity-60"
            >
              {spending ? "..." : "Kaufen"}
            </button>
          </div>
        </div>

        {/* Buy packs */}
        <div className="mt-8">
          <h3 className="font-display text-lg font-bold text-white flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-300" /> Münzen kaufen
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {packs.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                data-testid={`coin-pack-${p.id}`}
                className="rounded-2xl bg-[#131520] border border-white/10 hover:border-amber-400/40 p-5 text-center transition-all"
              >
                <CoinsIcon className="w-8 h-8 text-amber-300 mx-auto" />
                <p className="font-display text-2xl font-extrabold text-white mt-3">{p.coins.toLocaleString("de-DE")}</p>
                <p className="text-xs text-slate-400">Münzen</p>
                <button
                  data-testid={`buy-pack-${p.id}`}
                  onClick={() => buy(p)}
                  disabled={buying === p.id}
                  className="mt-4 w-full bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-bold rounded-xl py-2.5 active:scale-95 transition-all disabled:opacity-60"
                >
                  {buying === p.id ? "..." : `${(p.price / 100).toFixed(2).replace(".", ",")}€`}
                </button>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-500 mt-4">
            <Check className="w-3 h-3 inline mr-1" /> Sichere Zahlung über Stripe · Testkarte 4242 4242 4242 4242
          </p>
        </div>

        {/* History */}
        <div className="mt-8">
          <h3 className="font-display text-lg font-bold text-white flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-slate-400" /> Münz-Verlauf
          </h3>
          {history.length === 0 ? (
            <div className="text-center py-8 text-slate-500 rounded-2xl border border-white/5 bg-[#12141D]" data-testid="coins-history-empty">
              Noch keine Münz-Bewegungen.
            </div>
          ) : (
            <div className="rounded-2xl bg-[#131520] border border-white/5 divide-y divide-white/5" data-testid="coins-history">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${h.delta >= 0 ? "bg-emerald-500/15" : "bg-rose-500/15"}`}>
                    <CoinsIcon className={`w-4 h-4 ${h.delta >= 0 ? "text-emerald-300" : "text-rose-300"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{h.reason}</p>
                    <p className="text-xs text-slate-500">{new Date(h.created_at).toLocaleString("de-DE")}</p>
                  </div>
                  <span className={`font-semibold text-sm ${h.delta >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                    {h.delta >= 0 ? "+" : ""}{h.delta}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
